import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import sortBy from "lodash.sortby";
import sumBy from "lodash.sumby";
import { ChevronDown } from "lucide-react";
import React, { useMemo } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgTableWrapper, IgTypography } from "@intelligems/ig-theme";
import { OrganizationSummarySubscriptionProductDto } from "@intelligems/ig-types";
import { Box, Grid } from "@mui/material";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { ActionsColumn } from "@src/components/BillingPanel/components/ManagePlans/components/ManageSubscriptionsTable/renderers/ActionsColumn";
import { AmountColumn } from "@src/components/BillingPanel/components/ManagePlans/components/ManageSubscriptionsTable/renderers/AmountColumn";
import { CurrentPeriodColumn } from "@src/components/BillingPanel/components/ManagePlans/components/ManageSubscriptionsTable/renderers/CurrentPeriodColumn";
import { NameColumn } from "@src/components/BillingPanel/components/ManagePlans/components/ManageSubscriptionsTable/renderers/NameColumn";
import { SettingsColumn } from "@src/components/BillingPanel/components/ManagePlans/components/ManageSubscriptionsTable/renderers/SettingsColumn";
import { StatusColumn } from "@src/components/BillingPanel/components/ManagePlans/components/ManageSubscriptionsTable/renderers/StatusColumn";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";

const customOrder = ["active", "trialing", "cancelled", "canceled"];

interface ManageSubscriptionsTableProps {
  showAll?: boolean;
}

export const ManageSubscriptionsTable = ({
  showAll,
}: ManageSubscriptionsTableProps) => {
  const adminOrg = useGetOrganizationSummary();
  const [pastExpanded, setPastExpanded] = useImmer<boolean>(false);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      fontSize: "14px",
    };
  }, []);

  const data: OrganizationSummarySubscriptionProductDto[] = useMemo(() => {
    if (!adminOrg.isSuccess) {
      return [];
    }

    const deprecated: OrganizationSummarySubscriptionProductDto[] =
      adminOrg.data.org.organizationSubscriptions.flatMap((sub) => ({
        price: sub.price!,
        name: sub.name,
        interval: sub.interval!,
        intervalCount: 1,
        status: sub.status,
        stripeSubscriptionLinkId: "",
        stripeSubscriptionId: "",
        stripePriceId: "",
      }));

    const active: OrganizationSummarySubscriptionProductDto[] =
      adminOrg.data.org.stripeSubscriptionSummaries.map((sl) => {
        const pls = sl.stripeProductLinks.filter((pl) => !!pl.stripeProduct);

        return {
          price: sumBy(pls.map((pl) => pl.stripeProduct!.flatPrice)),
          name: Array.from(
            new Set(pls.map((pl) => pl.stripeProduct!.name))
          ).join(", "),
          interval: sl.interval,
          intervalCount: sl.intervalCount,
          status: sl.status,
          stripeSubscriptionId: sl.stripeSubscriptionId,
          stripeSubscriptionLinkId: sl.id,
          cancelAt: sl.cancelAt,
          stripePriceId: sl.stripePriceId,
          subscriptionSummary: sl,
        };
      });

    return sortBy([...deprecated, ...active], (item) => {
      const orderIndex = customOrder.indexOf(item.status);
      return orderIndex === -1 ? Infinity : orderIndex;
    });
  }, [adminOrg.isSuccess, adminOrg.data]);

  const currentSubscriptions = useMemo(() => {
    return data.filter((sub) => ["active", "trialing"].includes(sub.status));
  }, [data]);

  const pastSubscriptions = useMemo(() => {
    return data.filter(
      (sub) =>
        ["cancelled", "canceled"].includes(sub.status) ||
        !["active", "trialing"].includes(sub.status)
    );
  }, [data]);

  const columnDefs: ColDef<
    OrganizationSummarySubscriptionProductDto & { "": "" }
  >[] = useMemo(
    () => [
      {
        headerName: "Plan Name",
        field: "",
        minWidth: 230,
        cellRenderer: NameColumn,
      },

      {
        headerName: "Amount",
        field: "",
        cellRenderer: AmountColumn,
        minWidth: 110,
        maxWidth: 200,
        flex: 0.5,
      },
      {
        headerName: "Current Period",
        field: "",
        cellRenderer: CurrentPeriodColumn,
        maxWidth: 200,
        suppressHeaderMenuButton: true,
        flex: 0.5,
      },
      {
        headerName: "Status",
        field: "",
        cellRenderer: StatusColumn,
        suppressHeaderMenuButton: true,
        flex: 0.5,
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsColumn,
        suppressMenu: true,
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 280,
      },
      {
        field: "",
        cellRenderer: SettingsColumn,
        suppressMenu: true,
        maxWidth: 50,
        flex: 0.2,
        sortable: false,

        suppressHeaderMenuButton: true,
        resizable: false,
      },
    ],
    []
  );
  if (!adminOrg.isSuccess) {
    return <SkeletonGroup lines={3} />;
  }

  if (!data?.length) {
    return null;
  }

  return (
    <Grid container item rowSpacing={5}>
      <Grid item>
        <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
          Manage Plans
        </IgTypography>
      </Grid>
      <Grid
        item
        sx={{
          width: "100%",
          "& .ag-center-cols-clipper": {
            minHeight: "50px !important",
          },
        }}
      >
        <Grid item>
          <IgTableWrapper height={null} minHeight={"20px"}>
            <AgGridReact<OrganizationSummarySubscriptionProductDto>
              rowData={currentSubscriptions}
              rowHeight={80}
              domLayout={"autoHeight"}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              headerHeight={35}
              suppressCellFocus={true}
              suppressContextMenu={true}
              context={{
                showAll,
                agencies: adminOrg.data?.org?.agencies || [],
              }}
              suppressRowHoverHighlight={true}
            />
          </IgTableWrapper>
        </Grid>
        {pastSubscriptions.length > 0 && (
          <>
            <Box mt={1} mb={1}>
              <IgButton
                onClick={() => setPastExpanded(!pastExpanded)}
                startIcon={
                  <ChevronDown
                    style={{
                      transform: pastExpanded
                        ? "rotate(-180deg)"
                        : "rotate(0deg)",
                      transition: ".3s",
                    }}
                    size={16}
                  />
                }
                variant={"text"}
                color={"secondary"}
              >
                <IgTypography>
                  {pastSubscriptions.length} Past Subscription
                  {pastSubscriptions.length === 1 ? "" : "s"}
                </IgTypography>
              </IgButton>
            </Box>

            {pastExpanded && (
              <IgTableWrapper height={null} minHeight={"100px"}>
                <AgGridReact<OrganizationSummarySubscriptionProductDto>
                  rowData={pastSubscriptions}
                  rowHeight={80}
                  domLayout={"autoHeight"}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  headerHeight={35}
                  suppressCellFocus={true}
                  suppressContextMenu={true}
                  context={{
                    showAll,
                    agencies: adminOrg.data?.org?.agencies || [],
                  }}
                  suppressRowHoverHighlight={true}
                />
              </IgTableWrapper>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};
